"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useContext,
  createContext,
  Dispatch,
  SetStateAction,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useSearchParams } from "next/navigation";
import { FilterParams } from "@/interfaces/filterParams";
import { Organization } from "@/interfaces/organization";
import { organizationsMapOptions } from "@/queryOptions/organizations";
import { PersonWithVoices } from "@/queryOptions/persons";
import { voiceIdPersonMapOptions } from "@/queryOptions/voices";
import getDefaultFilterParams from "@/shared/getDefaultFilterParams";
import {
  updateQueryClientOrganizations,
  updateQueryClientPersons,
} from "@/utils/queryClientUpdate";

export const FilterParamsContext = createContext<{
  filterParams: FilterParams;
  setFilterParams: Dispatch<SetStateAction<FilterParams>>;
}>({
  filterParams: getDefaultFilterParams(),
  setFilterParams: () => null,
});

export function FilterParamsInput() {
  const { filterParams } = useContext(FilterParamsContext);

  return (
    <input
      type="hidden"
      name="filter-params"
      value={JSON.stringify(filterParams)}
    />
  );
}

export function EncodedFilterParamsInput() {
  const { filterParams } = useContext(FilterParamsContext);

  return (
    <input
      type="hidden"
      name="filter-params"
      value={btoa(JSON.stringify(filterParams))}
    />
  );
}

export function FilterParamsHydrator() {
  const searchParams = useSearchParams();
  const { setFilterParams } = useContext(FilterParamsContext);
  const { data: organizationsMap } = useQuery(organizationsMapOptions());
  const { data: voiceIdPersonMap } = useQuery(voiceIdPersonMapOptions());
  const queryClient = useQueryClient();

  useEffect(() => {
    const encodedFilterParams = searchParams?.get("filter-params");
    if (encodedFilterParams) {
      const decodedFilterParams: FilterParams = JSON.parse(
        atob(encodedFilterParams),
      );
      setFilterParams({
        ...getDefaultFilterParams(),
        ...decodedFilterParams,
      });
    }
  }, [searchParams, setFilterParams]);

  useEffect(() => {
    const encodedFilterParams = searchParams?.get("filter-params");
    if (encodedFilterParams) {
      const decodedFilterParams: FilterParams = JSON.parse(
        atob(encodedFilterParams),
      );
      const missingOrganizations = decodedFilterParams.organizations?.filter(
        (orgId) => !organizationsMap?.has(orgId),
      );
      if (missingOrganizations && missingOrganizations.length > 0) {
        const fetchMissingOrganizations = async () => {
          const response = await fetch(
            `/api/organizations?${new URLSearchParams({ ids: missingOrganizations.join(",") })}`,
          );
          const organizations: Organization[] = await response.json();

          updateQueryClientOrganizations(queryClient, organizations);
        };

        fetchMissingOrganizations();
      }
    }
  }, [organizationsMap, queryClient, searchParams]);

  useEffect(() => {
    const encodedFilterParams = searchParams?.get("filter-params");
    if (encodedFilterParams) {
      const decodedFilterParams: FilterParams = JSON.parse(
        atob(encodedFilterParams),
      );
      const missingVoiceIds = decodedFilterParams.voice_ids?.filter(
        (voiceId) => !voiceIdPersonMap?.has(voiceId),
      );
      if (missingVoiceIds && missingVoiceIds.length > 0) {
        const fetchMissingPersons = async () => {
          const searchParams = new URLSearchParams({
            search_type: "search_by_voice_id",
          });
          missingVoiceIds.forEach((voiceId) => {
            searchParams.append("voice_ids", voiceId.toString());
          });
          const response = await fetch(`/api/persons?${searchParams}`);
          const persons: PersonWithVoices[] = await response.json();

          updateQueryClientPersons(queryClient, persons);
        };

        fetchMissingPersons();
      }
    }
  }, [queryClient, searchParams, voiceIdPersonMap]);

  return null; // This component only handles side effects
}

export default function FilterParamsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [filterParams, setFilterParams] = useState<FilterParams>(
    getDefaultFilterParams(),
  );
  return (
    <FilterParamsContext.Provider value={{ filterParams, setFilterParams }}>
      {children}
    </FilterParamsContext.Provider>
  );
}
